import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { BottomNav } from '../components/BottomNav';
import { ArshHeader } from '../components/ArshHeader';
import { logger } from '../utils/logger';
import { BACKEND_URL } from '../utils/api';
import { ANIMAL_ICONS } from '../theme';
import { AuraRing } from '../components/AuraRing';
import { useHistoryClose } from '../hooks/useHistoryClose';
import { MemberCard } from '../components/MemberCard';
import { AuraTierRows } from '../components/AuraTierRows';
import { hiResAvatar } from '../utils/avatar';
import { GlobalBlissChat } from '../components/GlobalBlissChat';

/* 9 pillars — id matches memories.category AND users.profile_tag.
   Each pillar carries a signature accent colour used to theme the
   "Members by Pillar" modal when it opens (see PillarMembersModal).
   Empire (dragon) is placed FIRST so the row reads Empire → 8 pillars. */
const PILLARS = [
  { id: 'dragon',  label: 'Empire',  accent: '#E4A62A', accentSoft: '#F5C860', glow: 'rgba(228,166,42,0.55)' },
  { id: 'fashion', label: 'Fashion', accent: '#D4AF37', accentSoft: '#F5D96A', glow: 'rgba(212,175,55,0.55)' },
  { id: 'food',    label: 'Food',    accent: '#C8A95E', accentSoft: '#E8CE94', glow: 'rgba(200,169,94,0.50)' },
  { id: 'explore', label: 'Explore', accent: '#0F52BA', accentSoft: '#4A85E8', glow: 'rgba(15,82,186,0.55)' },
  { id: 'souls',   label: 'Soul',    accent: '#DC143C', accentSoft: '#FF4A6A', glow: 'rgba(220,20,60,0.55)' },
  { id: 'sporty',  label: 'Sport',   accent: '#10B981', accentSoft: '#4FE8B4', glow: 'rgba(16,185,129,0.55)' },
  { id: 'success', label: 'Success', accent: '#E5C44D', accentSoft: '#F5DE85', glow: 'rgba(229,196,77,0.55)' },
  { id: 'legacy',  label: 'Legacy',  accent: '#8B5CF6', accentSoft: '#B08CFF', glow: 'rgba(139,92,246,0.55)' },
  { id: 'art',     label: 'Art',     accent: '#2A9BD8', accentSoft: '#5BC0F0', glow: 'rgba(42,155,216,0.55)' },
];

const crestImg = (n) => `${process.env.PUBLIC_URL || ''}/society/${n}.jpg`;
/* Bliss Society crests — each shield is a crystal animal; tap → Top 10. */
const TIERS = [
  { id: 'pronoia', label: 'Pronoia', img: crestImg('fox'), accent: '#E8B830', accentSoft: '#FFE38A',
    tint: '#C8901A', rankText: '#2A1A02', glow: 'rgba(232,184,48,0.6)',
    border: ['#FFF1B8', '#E8B830', '#F5A623', '#FFE08A', '#B8860B', '#F7D774', '#FFF1B8'] },
  { id: 'aura', label: 'Aura', img: crestImg('panther'), accent: '#A9A6D6', accentSoft: '#E4E2FF',
    tint: '#1A1830', rankText: '#0A0A14', glow: 'rgba(140,130,220,0.5)',
    border: ['#2A2A3A', '#8D8AC0', '#0A0A12', '#C9C6F0', '#000000', '#5A5880', '#2A2A3A'] },
  { id: 'vibe', label: 'Vibes', img: crestImg('peacock'), accent: '#EEF1FF', accentSoft: '#FFFFFF',
    tint: '#9AA6C8', rankText: '#14161E', glow: 'rgba(235,240,255,0.55)',
    border: ['#FFFFFF', '#C9D2EA', '#F5F7FF', '#9FB0D8', '#FFFFFF', '#DCE3F5', '#FFFFFF'] },
];

const CREST_STAT = {
  pronoia: (u) => `${u.score || 0} pronoia`,
  aura: (u) => `${u.fused_count || 0} pillars fused`,
  vibe: (u) => `${u.score || 0} vibes`,
};

const TierModal = ({ tier, onClose, navigate }) => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  useHistoryClose(true, onClose);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios.get(`${BACKEND_URL}/api/discover/society-top?category=${tier.id}`)
      .then((r) => { if (!cancelled) setMembers(r.data || []); })
      .catch((e) => { logger.error('society-top', e); if (!cancelled) setMembers([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [tier.id]);

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="safe-top fixed inset-0 z-[60] bg-black flex flex-col overflow-hidden" data-testid={`tier-modal-${tier.id}`}>
      <img src={tier.img} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover pointer-events-none"
        style={{ opacity: 0.16, filter: 'blur(2px)' }}/>
      <div className="absolute inset-0 pointer-events-none"
        style={{ background: `radial-gradient(ellipse at 50% 0%, ${tier.tint}66, rgba(0,0,0,0.92) 60%)` }}/>
      <div className="relative z-10 flex flex-col items-center gap-1.5 px-4 pt-3 pb-3 flex-shrink-0"
        style={{ borderBottom: `1px solid ${tier.accent}55`, background: 'linear-gradient(180deg, rgba(0,0,0,0.35), rgba(0,0,0,0.75))' }}>
        <button type="button" onClick={onClose}
          className="absolute top-3 left-3 w-9 h-9 rounded-full flex items-center justify-center"
          style={{ border: `1px solid ${tier.accent}77`, background: 'rgba(0,0,0,0.55)', color: tier.accent }}
          aria-label="Back" data-testid="tier-modal-back">
          <ArrowLeft size={16}/>
        </button>
        <div className="tier-shield overflow-hidden" style={{ width: 54, height: 60, padding: 1.5,
          background: `linear-gradient(120deg, ${tier.border.join(', ')})`, boxShadow: `0 0 18px ${tier.glow}` }}>
          <img src={tier.img} alt={tier.label} className="tier-shield w-full h-full object-cover"/>
        </div>
        <div className="font-['Cinzel'] uppercase tracking-[0.32em] text-lg leading-none"
          style={{ color: tier.accentSoft, textShadow: `0 0 12px ${tier.glow}` }} data-testid="tier-modal-title">
          {tier.label}
        </div>
        <div className="font-['Outfit'] text-[9px] uppercase tracking-[0.34em]" style={{ color: `${tier.accent}CC` }}>
          Top 10 · Bliss Society
        </div>
      </div>

      <div className={`relative z-10 flex-1 min-h-0 ${tier.id === 'aura' ? 'overflow-hidden' : 'overflow-y-auto px-4 py-5'}`} data-testid="tier-medal-list">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-10 h-10 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: tier.accent }}/>
          </div>
        ) : tier.id === 'aura' ? (
          <AuraTierRows members={members} tier={tier} navigate={navigate} />
        ) : members.length ? (
          <div className="grid grid-cols-2 gap-3 max-w-xl mx-auto"
            style={{ paddingBottom: 'calc(24px + var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)))' }}>
            {members.map((u, idx) => (
              <MemberCard key={u.email || idx} u={u} idx={idx} accent={tier.accent} rank={u.rank}
                stat={CREST_STAT[tier.id](u)} crest={tier} testId={`tier-member-${tier.id}-${idx}`}
                onOpen={() => navigate(`/user/${encodeURIComponent(u.email)}`)}/>
            ))}
          </div>
        ) : (
          <div className="text-center py-20 font-['Cormorant_Garamond'] italic" style={{ color: `${tier.accent}99` }}>
            no {tier.label.toLowerCase()} members yet
          </div>
        )}
      </div>
    </motion.div>
  );
};

const PillarBox = ({ p, active, onClick, testId }) => {
  const icon = ANIMAL_ICONS[p.id];
  return (
    <motion.button whileTap={{ scale: 0.94 }} onClick={onClick} data-testid={testId}
      className="flex flex-col items-center justify-center rounded-xl py-2 px-1"
      style={{
        background: active ? 'linear-gradient(180deg, rgba(212,175,55,0.22), rgba(10,10,16,0.9))' : 'rgba(10,10,16,0.6)',
        border: active ? '1px solid #D4AF37' : '1px solid rgba(255,255,255,0.08)',
        boxShadow: active ? '0 0 14px rgba(212,175,55,0.35)' : 'none',
      }}>
      <div className="w-8 h-8 rounded-full overflow-hidden border border-black/20 mb-0.5">
        {icon && <img src={icon.src} alt={p.label} className="w-full h-full object-cover" style={{ objectPosition: icon.pos }}/>}
      </div>
      <div className={`font-['Cormorant_Garamond'] text-[11px] leading-tight ${active ? 'text-[#D4AF37]' : 'text-white/85'}`}>{p.label}</div>
    </motion.button>
  );
};

/* PillarPickerModal — opens as a full-screen page when the user taps
   the "Bliss Global Feeds" or "Bliss Global Users" master tab.
   • Header: centered title + back arrow on the left.
   • Row: 9 pillar icons horizontally (Empire first). Empire is the
     "all" aggregator — selecting it shows every pillar's content
     interleaved. Tapping any other icon filters to just that pillar.
   • Body: feed content (mode='feeds') or member cards (mode='users')
     for the currently active pillar, rendered inline (no extra tap). */
const PillarPickerModal = ({ mode, onClose, navigate }) => {
  useHistoryClose(true, onClose);
  const [active, setActive] = useState('dragon'); // Empire by default = all
  const [items, setItems]   = useState([]);
  const [loading, setLoading] = useState(true);
  const title = mode === 'feeds' ? 'Bliss Global Feeds' : 'Bliss Global Users';
  const sub   = mode === 'feeds' ? 'Every pillar · every chronicle' : 'The Order · by pillar';

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    // Empire (dragon) → no category filter = ALL. Others → filter to that pillar.
    const isEmpire = active === 'dragon';
    const url = mode === 'feeds'
      ? `${BACKEND_URL}/api/discover/live-feed?limit=60${isEmpire ? '' : `&category=${active}`}`
      : `${BACKEND_URL}/api/users/discover?sort=recent${isEmpire ? '' : `&profile_tag=${active}`}`;
    axios.get(url)
      .then((r) => { if (!cancelled) setItems(r.data || []); })
      .catch(() => { if (!cancelled) setItems([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [active, mode]);

  const activePillar = PILLARS.find((p) => p.id === active) || PILLARS[0];
  const accent = activePillar.accent;

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="safe-top fixed inset-0 z-[120] flex flex-col bg-black"
      data-testid={`pillar-picker-${mode}`}>
      {/* Header — centered title, back arrow absolute-left */}
      <div className="flex-shrink-0 relative px-4 py-3.5 border-b"
        style={{
          background: 'linear-gradient(180deg, rgba(212,175,55,0.14), rgba(0,0,0,0.9))',
          borderColor: 'rgba(212,175,55,0.35)',
        }}>
        <button type="button" onClick={onClose}
          data-testid={`pillar-picker-${mode}-back`}
          className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full flex items-center justify-center"
          style={{
            background: 'rgba(212,175,55,0.14)',
            border: '1px solid #D4AF37',
            boxShadow: '0 0 12px rgba(212,175,55,0.35)',
          }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
            stroke="#FFE9A0" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6"/>
          </svg>
        </button>
        <div className="text-center">
          <div className="font-['Cinzel'] uppercase tracking-[0.26em] text-[13px]"
            style={{ color: '#FFE9A0', textShadow: '0 0 10px rgba(212,175,55,0.35)' }}>
            {title}
          </div>
          <div className="font-['Outfit'] italic text-[10px] mt-0.5"
            style={{ color: 'rgba(255,233,160,0.65)' }}>
            {sub}
          </div>
        </div>
      </div>

      {/* Horizontal row of 9 pillar icons — Empire first, always visible */}
      <div className="flex-shrink-0 px-1 py-2 border-b"
        style={{ borderColor: 'rgba(212,175,55,0.18)', background: 'rgba(10,10,16,0.65)' }}>
        <div className="grid grid-cols-9 gap-0.5" data-testid={`pillar-picker-${mode}-row`}>
          {PILLARS.map((p) => {
            const icon = ANIMAL_ICONS[p.id];
            const isActive = active === p.id;
            return (
              <motion.button key={p.id} whileTap={{ scale: 0.92 }}
                onClick={() => setActive(p.id)}
                data-testid={`pillar-picker-${mode}-${p.id}`}
                className="flex flex-col items-center justify-start py-1">
                <div className="rounded-full overflow-hidden transition-all"
                  style={{
                    width: isActive ? 40 : 34,
                    height: isActive ? 40 : 34,
                    border: `${isActive ? 2 : 1.5}px solid ${p.accent}${isActive ? 'ff' : '88'}`,
                    boxShadow: isActive
                      ? `0 0 14px ${p.glow}, 0 0 4px ${p.accent}`
                      : `0 0 6px ${p.glow}`,
                  }}>
                  {icon && <img src={icon.src} alt={p.label}
                    className="w-full h-full object-cover"
                    style={{ objectPosition: icon.pos }}/>}
                </div>
                <div className="mt-1 font-['Cormorant_Garamond'] leading-none text-center"
                  style={{
                    color: isActive ? p.accentSoft : `${p.accentSoft}99`,
                    fontSize: isActive ? 10.5 : 9.5,
                    fontWeight: isActive ? 600 : 400,
                  }}>
                  {p.label}
                </div>
              </motion.button>
            );
          })}
        </div>
      </div>

      {/* Body — feed items or member cards, based on mode + active pillar */}
      <div className="flex-1 overflow-y-auto pb-8">
        {loading ? (
          <div className="flex items-center justify-center py-10">
            <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin"/>
          </div>
        ) : items.length === 0 ? (
          <div className="text-center text-white/40 text-sm py-16 font-['Outfit']">
            {mode === 'feeds' ? 'No chronicles in this pillar yet.' : 'No members in this pillar yet.'}
          </div>
        ) : mode === 'feeds' ? (
          /* FEED CARDS — Instagram-style like FeedModal */
          items.map((m, idx) => (
            <motion.div key={m.id || idx} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: (idx % 6) * 0.04 }}
              className="mx-3 my-3 rounded-2xl overflow-hidden bg-[#05050A]"
              style={{ border: `1px solid ${accent}30` }}
              data-testid={`pillar-picker-feeds-item-${idx}`}>
              <button type="button"
                onClick={() => m.user_email && navigate(`/user/${encodeURIComponent(m.user_email)}`)}
                className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-white/5 transition-colors"
                disabled={!m.user_email}>
                <div className="w-9 h-9 rounded-full overflow-hidden flex-shrink-0"
                  style={{ border: `1.5px solid ${accent}`, boxShadow: `0 0 10px ${accent}55` }}>
                  {m.author_profile_picture
                    ? <img src={m.author_profile_picture} alt="" className="w-full h-full object-cover"/>
                    : <div className="w-full h-full flex items-center justify-center text-xs font-['Cormorant_Garamond']"
                        style={{ background: `${accent}22`, color: accent }}>
                        {(m.author_nickname || 'A')[0].toUpperCase()}
                      </div>}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-['Cormorant_Garamond'] text-white text-[15px] leading-tight truncate">
                    {m.author_nickname || 'Chronicle'}
                  </div>
                  <div className="font-['Outfit'] text-[9px] uppercase tracking-[0.15em]"
                    style={{ color: `${accent}b0` }}>
                    {m.category || 'chronicle'}
                  </div>
                </div>
              </button>
              <img src={m.image_url} alt="" className="w-full block" loading="lazy"/>
            </motion.div>
          ))
        ) : (
          /* MEMBER CARDS — 2-col grid, AuraRing avatar + nickname + View Vibes.
             Matches the existing PillarMembersModal reference exactly. */
          <div className="grid grid-cols-2 gap-3 px-3 py-3">
            {items.map((u, idx) => {
              const userAccent = active === 'dragon'
                ? (PILLARS.find((p) => p.id === u.profile_tag && p.id !== 'dragon') || PILLARS[(idx % 8) + 1]).accent
                : accent;
              return (
                <MemberCard key={u.id || u.email || idx} u={u} idx={idx} accent={userAccent}
                  testId={`pillar-picker-users-item-${idx}`}
                  onOpen={() => u.email && navigate(`/user/${encodeURIComponent(u.email)}`)}/>
              );
            })}
          </div>
        )}
      </div>
    </motion.div>
  );
};

const FeedModal = ({ feedTag, feedItems, onClose, navigate }) => {
  useHistoryClose(true, onClose);
  const pillar = PILLARS.find((x) => x.id === feedTag);
  const accent = pillar?.accent || '#D4AF37';
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="safe-top fixed inset-0 z-50 bg-black" data-testid="feed-modal">
      <div className="sticky top-0 z-10 flex items-center justify-center px-4 py-3 backdrop-blur"
        style={{
          background: `linear-gradient(180deg, ${pillar?.glow || 'rgba(212,175,55,0.35)'}, rgba(0,0,0,0.95))`,
          borderBottom: `1px solid ${accent}55`,
        }}>
        <span className="font-['Cormorant_Garamond'] text-lg capitalize" style={{ color: accent }}>
          {pillar?.label} Feed
        </span>
      </div>
      <div className="overflow-y-auto h-[calc(100vh-56px)] pb-8">
        {feedItems.length ? feedItems.map((m, idx) => (
          <motion.div key={m.id || idx} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: (idx % 6) * 0.05 }}
            className="mx-3 my-3 rounded-2xl overflow-hidden bg-[#05050A]" style={{ border: `1px solid ${accent}30` }}
            data-testid={`feed-item-${idx}`}>
            {/* Instagram-style author bar — clickable, goes to /user/{email} */}
            <button
              type="button"
              onClick={() => m.user_email && navigate(`/user/${encodeURIComponent(m.user_email)}`)}
              className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-white/5 transition-colors"
              data-testid={`feed-author-${idx}`}
              disabled={!m.user_email}
            >
              <div className="w-9 h-9 rounded-full overflow-hidden flex-shrink-0"
                style={{
                  border: `1.5px solid ${accent}`,
                  boxShadow: `0 0 10px ${accent}55, inset 0 0 6px ${accent}22`,
                }}>
                {m.author_profile_picture
                  ? <img src={m.author_profile_picture} alt="" className="w-full h-full object-cover"/>
                  : <div className="w-full h-full flex items-center justify-center text-xs font-['Cormorant_Garamond']"
                      style={{ background: `${accent}22`, color: accent }}>
                      {(m.author_nickname || 'A')[0].toUpperCase()}
                    </div>}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-['Cormorant_Garamond'] text-white text-[15px] leading-tight truncate">
                  {m.author_nickname || 'Chronicle'}
                </div>
                <div className="font-['Outfit'] text-[9px] uppercase tracking-[0.15em]" style={{ color: `${accent}b0` }}>
                  {m.category || 'chronicle'}
                </div>
              </div>
            </button>
            <img src={m.image_url} alt="" className="w-full block" loading="lazy"/>
          </motion.div>
        )) : (
          <div className="text-center text-white/40 text-sm py-16 font-['Outfit']">No chronicles in this pillar yet.</div>
        )}
      </div>
    </motion.div>
  );
};

/* Members by Pillar — themed modal that expands from the tapped pillar
   tile.  Colour, icon and glow are all inherited from the pillar's
   entry in PILLARS above, so each pillar feels unmistakably its own. */
const PillarMembersModal = ({ pillar, onClose, navigate }) => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  useHistoryClose(true, onClose);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios.get(`${BACKEND_URL}/api/users/discover?sort=recent&profile_tag=${pillar.id}`)
      .then((r) => { if (!cancelled) setMembers(r.data || []); })
      .catch((e) => { logger.error('pillar-members', e); if (!cancelled) setMembers([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [pillar.id]);

  const icon = ANIMAL_ICONS[pillar.id];

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="safe-top fixed inset-0 z-[60]" data-testid={`pillar-modal-${pillar.id}`}
      style={{
        background: '#05050A',
        backgroundImage: `radial-gradient(circle at 50% -10%, ${pillar.accent}33 0%, transparent 55%)`,
      }}>
      {/* Themed header — inherits pillar's colour + icon */}
      <div className="sticky top-0 z-10 flex items-center gap-3 px-4 py-4 backdrop-blur"
        style={{
          background: `linear-gradient(180deg, ${pillar.glow}, rgba(0,0,0,0.92))`,
          borderBottom: `1px solid ${pillar.accent}55`,
          boxShadow: `0 8px 22px -8px ${pillar.glow}`,
        }}>
        <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0"
          style={{
            border: `1.5px solid ${pillar.accent}`,
            boxShadow: `0 0 22px ${pillar.glow}, inset 0 0 12px ${pillar.accent}44`,
          }}>
          {icon && <img src={icon.src} alt={pillar.label} className="w-full h-full object-cover" style={{ objectPosition: icon.pos }}/>}
        </div>
        <div>
          <div className="font-['Outfit'] text-[9px] uppercase tracking-[0.3em]" style={{ color: pillar.accent }}>The Order of</div>
          <div className="font-['Cormorant_Garamond'] text-2xl leading-none" style={{ color: pillar.accent }}>{pillar.label}</div>
        </div>
      </div>

      <div className="overflow-y-auto h-[calc(100vh-88px)] px-4 py-6">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-10 h-10 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: pillar.accent }}/>
          </div>
        ) : members.length ? (
          <div className="grid grid-cols-2 gap-3 max-w-3xl mx-auto pb-16">
            {members.map((u, idx) => (
              <motion.button
                key={u.email || idx}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: (idx % 8) * 0.04 }}
                onClick={() => navigate(`/user/${encodeURIComponent(u.email)}`)}
                data-testid={`pillar-member-${pillar.id}-${idx}`}
                className="relative rounded-2xl p-3 flex flex-col items-center text-center"
                style={{
                  background: `linear-gradient(180deg, ${pillar.accent}18, #05050A)`,
                  border: `1px solid ${pillar.accent}55`,
                  boxShadow: `0 6px 18px rgba(0,0,0,0.5), 0 0 22px ${pillar.accent}22 inset`,
                }}>
                <div className="w-16 h-16 mb-2">
                  <AuraRing size={64} data={u.aura_ring || { fused: {}, gold: false }}>
                    <div className="w-14 h-14 rounded-full overflow-hidden border-2"
                      style={{ borderColor: `${pillar.accent}66`, boxShadow: `0 0 16px ${pillar.accent}44` }}>
                      {u.profile_picture
                        ? <img src={u.profile_picture} alt={u.nickname} className="w-full h-full object-cover"/>
                        : <div className="w-full h-full" style={{ background: `linear-gradient(135deg, ${pillar.accent}55, #05050A)` }}/>}
                    </div>
                  </AuraRing>
                </div>
                <div className="font-['Cormorant_Garamond'] text-white text-[15px] leading-tight truncate w-full">
                  {u.nickname || u.email?.split('@')[0]}
                </div>
                <div className="font-['Outfit'] text-[9px] uppercase tracking-[0.15em] mt-0.5"
                  style={{ color: `${pillar.accent}bb` }}>
                  {pillar.label}
                </div>
              </motion.button>
            ))}
          </div>
        ) : (
          <div className="text-center py-20 font-['Outfit'] text-white/40">
            No members yet in the {pillar.label} order.
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default function Discover() {
  const [top3, setTop3]           = useState([]);
  const [dashboard, setDashboard] = useState([]);
  const [feedTag, setFeedTag]     = useState(null);
  const [feedItems, setFeedItems] = useState([]);
  const [openPillar, setOpenPillar] = useState(null);
  const [openTier, setOpenTier]   = useState(null);
  const [loading, setLoading]     = useState(true);
  const [openChat, setOpenChat] = useState(false);
  const [openPicker, setOpenPicker] = useState(null); // 'feeds' | 'users' | null
  const navigate                  = useNavigate();

  const loadCore = useCallback(async () => {
    try {
      const [t, f] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/discover/top-ranked`),
        axios.get(`${BACKEND_URL}/api/discover/live-feed?limit=40`),
      ]);
      setTop3(t.data || []);
      setDashboard(f.data || []);
    } catch (e) { logger.error('core', e); }
    finally    { setLoading(false); }
  }, []);

  useEffect(() => { loadCore(); }, [loadCore]);

  useEffect(() => {
    if (!feedTag) { setFeedItems([]); return; }
    axios.get(`${BACKEND_URL}/api/discover/live-feed?limit=60&category=${feedTag}`)
      .then((r) => setFeedItems(r.data || [])).catch(() => setFeedItems([]));
  }, [feedTag]);

  // top3 is used for header counts only (not for card rendering — the
  // podium tiles are TIER gates, not user cards).
  const podiumTop = top3;  // retained for future stat display

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-black">
      <div className="w-14 h-14 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  const marqueeTiles = [...dashboard, ...dashboard];

  return (
    <div className="relative bg-black flex flex-col overflow-hidden" style={{ height: '100dvh', marginBottom: -80 }} data-testid="discover-page">
      <ArshHeader />

      <div className="flex-1 min-h-0 max-w-5xl mx-auto w-full px-3 pt-1 flex flex-col gap-2 overflow-hidden"
        style={{ paddingBottom: 'calc(100px + var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)))' }}>

        {/* 1. TOP 3 — Bliss tier gates (untouched sizing/colours) */}
        <section data-testid="discover-top-3" className="flex-shrink-0">
          <div className="text-center mb-1.5">
            <span className="font-['Outfit'] text-[9px] uppercase tracking-[0.3em] text-[#D4AF37]/60">The Society · Top 10</span>
            <h2 className="font-['Cormorant_Garamond'] text-lg font-light text-white leading-tight">Bliss <span className="text-[#D4AF37]">Crests</span></h2>
          </div>
          <div className="grid grid-cols-3 gap-2.5 px-1">
            {TIERS.map((t, i) => (
              <motion.button
                key={t.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                whileTap={{ scale: 0.96 }}
                onClick={() => setOpenTier(t)}
                data-testid={`top3-tier-${t.id}`}
                className="tier-shield relative mx-auto w-full"
                style={{ maxWidth: 100, aspectRatio: '1 / 1.12', padding: 1.5,
                  background: `linear-gradient(120deg, ${t.border.join(', ')})`, backgroundSize: '300% 300%',
                  '--tg': t.glow, animation: `tierShimmer 6s ease-in-out infinite, tierBreath 4.5s ease-in-out ${i * 1.5}s infinite` }}>
                <div className="tier-shield relative w-full h-full overflow-hidden" style={{ background: '#05050A' }}>
                  <img src={t.img} alt={t.label} className="absolute inset-0 w-full h-full object-cover" data-testid={`top3-tier-${t.id}-img`}/>
                  <div className="absolute inset-x-0 bottom-0 pointer-events-none"
                    style={{ height: '42%', background: 'linear-gradient(180deg, transparent, rgba(0,0,0,0.88))' }}/>
                  <span className="absolute inset-x-0 text-center font-['Cinzel'] uppercase leading-none"
                    style={{ bottom: '13%', fontSize: 10.5, letterSpacing: '0.2em', paddingLeft: '0.2em',
                      color: t.accentSoft, textShadow: `0 0 8px ${t.glow}, 0 1px 2px #000` }}>{t.label}</span>
                </div>
              </motion.button>
            ))}
          </div>
          <div className="hidden">{podiumTop.length}</div>
        </section>

        {/* 2 + 3. MASTER TABS — stacked vertically, tap to open a
             full-screen pillar picker (feed browser). */}
        <section data-testid="discover-master-tabs" className="flex-shrink-0 space-y-2">
          {[
            { key: 'feeds', label: 'Bliss Global Feeds', sub: 'Every pillar · every chronicle' },
            { key: 'users', label: 'Bliss Global Users', sub: 'The Order · by pillar' },
            { key: 'chat',  label: 'Global Bliss Chat',  sub: 'Live · a new icon room every hour' },
          ].map((t) => (
            <motion.button
              key={t.key}
              whileTap={{ scale: 0.98 }}
              onClick={() => (t.key === 'chat' ? setOpenChat(true) : setOpenPicker(t.key))}
              data-testid={`discover-master-${t.key}`}
              className="w-full rounded-2xl px-4 py-2.5 text-center"
              style={{
                background: 'linear-gradient(135deg, rgba(212,175,55,0.18), rgba(184,115,51,0.06) 55%, #05050A)',
                border: '1px solid rgba(212,175,55,0.55)',
                boxShadow: '0 6px 20px rgba(0,0,0,0.55), inset 0 0 16px rgba(212,175,55,0.14)',
              }}>
              <div className="font-['Cinzel'] uppercase tracking-[0.26em] text-[13px]"
                style={{ color: '#FFE9A0', textShadow: '0 0 8px rgba(212,175,55,0.35)' }}>
                {t.label}
              </div>
              <div className="font-['Outfit'] italic text-[10.5px] mt-0.5"
                style={{ color: 'rgba(255,233,160,0.65)' }}>
                {t.sub}
              </div>
            </motion.button>
          ))}
        </section>

        {/* 4. DASHBOARD — latest public uploads marquee (image first, author badge) */}
        <section data-testid="discover-dashboard" className="flex-1 min-h-0 flex flex-col">
          <div className="text-center mb-1.5">
            <span className="font-['Outfit'] text-[9px] uppercase tracking-[0.3em] text-[#D4AF37]/60">The Living Gallery</span>
            <h2 className="font-['Cormorant_Garamond'] text-base font-light text-white leading-tight">Latest <span className="text-[#D4AF37]">Chronicles</span></h2>
          </div>
          {dashboard.length ? (
            <div className="relative overflow-hidden -mx-3 flex-1 min-h-0"
              style={{
                maskImage: 'linear-gradient(90deg, transparent 0%, #000 8%, #000 92%, transparent 100%)',
                WebkitMaskImage: 'linear-gradient(90deg, transparent 0%, #000 8%, #000 92%, transparent 100%)',
              }}>
              <div className="marquee-track flex gap-3 py-2 h-full"
                style={{ animation: `marquee-scroll ${Math.max(30, dashboard.length * 3)}s linear infinite` }}>
                {marqueeTiles.map((m, idx) => {
                  const upload = m.image_url || m.thumbnail_url;
                  const pc = PILLARS.find((p) => p.id === m.category) || PILLARS[(idx % 8) + 1];
                  return (
                    <div key={idx} className="shrink-0 rounded-xl relative cursor-pointer"
                      style={{
                        height: '100%', maxHeight: 300, aspectRatio: '3 / 4', padding: 1.5,
                        background: `linear-gradient(160deg, ${pc.accentSoft}, ${pc.accent} 35%, #05050A 60%, ${pc.accent})`,
                        boxShadow: `0 10px 26px rgba(0,0,0,0.6), 0 0 14px ${pc.glow}`,
                      }}
                      data-testid={`chronicle-tile-${idx}`}
                      onClick={() => m.user_email && navigate(`/user/${encodeURIComponent(m.user_email)}`)}>
                      <div className="w-full h-full rounded-[10px] overflow-hidden relative" style={{ background: `linear-gradient(180deg, ${pc.accent}22, #05050A)` }}>
                      {upload ? (
                        <img src={upload} alt="" className="w-full h-full object-cover" loading="lazy" data-testid={`chronicle-image-${idx}`}/>
                      ) : (
                        <div className="w-full h-full flex items-center justify-center font-['Cormorant_Garamond'] italic text-3xl" style={{ color: `${pc.accent}99` }}>
                          {m.author_nickname?.[0] || '·'}
                        </div>
                      )}
                      <div className="chron-beam absolute inset-y-0 pointer-events-none"
                        style={{ width: '45%', left: 0, animationDelay: `${(idx % 6) * 0.9}s`,
                          background: `linear-gradient(100deg, transparent, ${pc.accentSoft}38, transparent)` }}/>
                      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/95 to-transparent px-2 pt-7 pb-2 flex items-center justify-center gap-1.5">
                        {m.author_profile_picture && (
                          <img src={hiResAvatar(m.author_profile_picture)} alt="" className="w-5 h-5 rounded-full object-cover flex-shrink-0"
                            style={{ border: `1px solid ${pc.accent}` }}/>
                        )}
                        <div className="text-[11px] font-['Outfit'] uppercase tracking-wide truncate"
                          style={{ color: pc.accentSoft, textShadow: `0 0 8px ${pc.glow}` }}>
                          {m.author_nickname}
                        </div>
                      </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : <div className="text-center text-white/30 text-xs py-2 font-['Outfit']">No chronicles yet.</div>}
        </section>
      </div>

      {/* Pillar Picker (Feed browser or Users browser) — full-screen page */}
      <AnimatePresence>
        {openChat && <GlobalBlissChat onClose={() => setOpenChat(false)}/>}
        {openPicker && (
          <PillarPickerModal
            mode={openPicker}
            onClose={() => setOpenPicker(null)}
            navigate={navigate}/>
        )}
      </AnimatePresence>

      {/* Tier modal (Gold / Silver / Bronze) — closes via device back gesture */}
      <AnimatePresence>
        {openTier && <TierModal tier={openTier} onClose={() => setOpenTier(null)} navigate={navigate} />}
      </AnimatePresence>

      {/* Pillar Members modal — themed by pillar */}
      <AnimatePresence>
        {openPillar && <PillarMembersModal pillar={openPillar} onClose={() => setOpenPillar(null)} navigate={navigate} />}
      </AnimatePresence>

      {/* Feed modal — closes via device back gesture */}
      <AnimatePresence>
        {feedTag && (
          <FeedModal
            feedTag={feedTag}
            feedItems={feedItems}
            onClose={() => setFeedTag(null)}
            navigate={navigate}
          />
        )}
      </AnimatePresence>

      <BottomNav activeTab="discover" onTabChange={(t) => { if (t !== 'discover') navigate('/'); }}/>

      <style>{`
        .no-scrollbar::-webkit-scrollbar{display:none;}
        .no-scrollbar{-ms-overflow-style:none;scrollbar-width:none;}
        @keyframes marquee-scroll { from { transform: translate3d(0,0,0); } to { transform: translate3d(-50%,0,0); } }
        .marquee-track { width: max-content; will-change: transform; }
        .marquee-track:hover { animation-play-state: paused; }
        .tier-shield { border-radius: 50% 50% 50% 50% / 42% 42% 58% 58%; }
        .crest-sheen { animation: crestSheen 6s ease-in-out infinite; transform: translateX(-100%); }
        @keyframes crestSheen { 0% { transform: translateX(-100%); } 50%,100% { transform: translateX(100%); } }
        @keyframes tierBreath { 0%,40%,100% { box-shadow: 0 6px 18px rgba(0,0,0,0.55), 0 0 8px var(--tg); } 18% { box-shadow: 0 6px 18px rgba(0,0,0,0.55), 0 0 26px var(--tg), 0 0 48px var(--tg); } }
        @keyframes tierShimmer { 0%,100% { background-position: 0% 50%; } 50% { background-position: 100% 50%; } }
        .chron-beam { animation: chronBeam 5.5s ease-in-out infinite; transform: translateX(-120%); will-change: transform; }
        @keyframes chronBeam { 0% { transform: translateX(-120%); } 55%,100% { transform: translateX(260%); } }
      `}</style>
    </div>
  );
}
